/**
 * S-02 Signup — DOB gate (V1 auth-profiles-gdpr). FIRST step, before anything
 * else. Dead serious: ©safety.age_gate, no dark humor on this screen.
 *
 * Continue is disabled until a plausible date is entered. Submit hits
 * POST /auth/dob-gate (server checks the SM-12 device underage_lock AND the
 * DOB): 204 → carry the DOB to S-04; 451 UNDERAGE → hard lock to S-03 (the
 * server wrote the device-keyed lock row; a local marker mirrors it so a
 * refresh lands straight on S-03 — the authoritative lock stays server-side).
 */
import { useState, type FormEvent } from "react";
import { Link, useNavigate } from "react-router-dom";
import { copy } from "@humble/shared";
import { Button, Card, IconBrokenHeart } from "../../design/index.js";
import { api, getDeviceFp, isApiError } from "../../lib/api.js";
import {
  readUnderageLockRef,
  setSignupDob,
  writeUnderageLockRef,
} from "./signup-flow.js";
import { UnderageLock } from "./underage-lock.js";
import "./auth.css";

function validateDob(dob: string): string | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(dob)) return "Enter your full date of birth.";
  const date = new Date(`${dob}T00:00:00Z`);
  if (Number.isNaN(date.getTime())) return "That date doesn't exist.";
  const now = new Date();
  if (date.getTime() > now.getTime()) return "A date of birth can't be in the future.";
  const years = (now.getTime() - date.getTime()) / (365.25 * 24 * 3600 * 1000);
  if (years > 120) return "Please enter your real date of birth.";
  return null;
}

export function AgeGateScreen() {
  const navigate = useNavigate();
  const [dob, setDob] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // S-02 checks the lock on load: a device already locked routes straight to
  // S-03 regardless of the DOB entered (the marker mirrors the server row).
  const [lockRef, setLockRef] = useState<string | null>(readUnderageLockRef());

  if (lockRef) return <UnderageLock lockRef={lockRef} />;

  const validation = dob ? validateDob(dob) : null;
  const canContinue = dob !== "" && validation === null;

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!canContinue || busy) return;
    setBusy(true);
    setError(null);
    try {
      await api.post("/auth/dob-gate", { body: { dob, device_fp: getDeviceFp() } });
      setSignupDob(dob);
      navigate("/signup/account");
    } catch (err) {
      if (isApiError(err, "UNDERAGE")) {
        const ref = typeof err.details?.lock_ref === "string" ? err.details.lock_ref : "unavailable";
        writeUnderageLockRef(ref);
        setLockRef(ref);
      } else if (isApiError(err)) {
        setError(err.message);
      } else {
        setError("Network error — try again.");
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="auth auth--center" data-screen="S-02">
      <div className="auth__brand">
        <IconBrokenHeart width={24} height={24} style={{ color: "var(--accent)" }} />
        humble
      </div>
      <Card padding="lg">
        <h1 className="auth__title">Your date of birth</h1>
        <p className="auth__serious">{copy.safety.age_gate}</p>
        <form onSubmit={onSubmit}>
          <div className="field">
            <label htmlFor="signup-dob">Date of birth</label>
            <input
              id="signup-dob"
              type="date"
              required
              max={new Date().toISOString().slice(0, 10)}
              value={dob}
              onChange={(e) => setDob(e.target.value)}
            />
          </div>
          {validation && (
            <p className="field__error" role="alert">
              {validation}
            </p>
          )}
          {error && (
            <p className="field__error" role="alert">
              {error}
            </p>
          )}
          <Button type="submit" block variant="primary" size="lg" disabled={!canContinue} loading={busy}>
            Continue
          </Button>
        </form>
      </Card>
      <div className="auth__footer">
        <Link to="/">Back to sign in</Link>
        <Link to="/safety">Safety Center</Link>
        <Link to="/legal/terms">Terms</Link>
        <Link to="/legal/privacy">Privacy</Link>
      </div>
    </div>
  );
}
